import { useQuery } from '@apollo/client/react';
import { faBug } from '@fortawesome/free-solid-svg-icons';
import {
  faArrowRightFromBracket,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '@teamexos/fit-shared';
import exosLogo2x from 'assets/logo@2x.png';
import exosLogo from 'assets/logo.png';
import Banner from 'components/common/Banner';
import useMixpanel from 'hooks/useMixpanel';
import useSplit from 'hooks/useSplit';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { usePlaybookTheme } from 'playbook';
import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { startViewTransition } from 'utils/helpers';
import { SelfDocument } from '../../graphql/types';

const Header = () => {
  const track = useMixpanel();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { data: userData } = useQuery(SelfDocument);
  const { flags } = useSplit();
  const theme = usePlaybookTheme();

  const location = useLocation();
  const showBanner = location.pathname === '/';

  const showDeveloperMenu = flags['show-developer-menu'];

  useEffect(() => {
    if (userData?.self && !userData.self.onboarding.isCompleted) {
      navigate('/complete-on-mobile');
    }
  });

  const avatar = `${userData?.self?.avatar?.basePath}/${userData?.self?.avatar?.id}/84x84.jpg`;
  const handleLogout = async () => {
    track('User Action', {
      event_name: 'menu_sign_out',
      event_type: 'screen interaction',
      event_category: 'menu',
      event_location: 'menu',
    });
    await signOut(false);
  };
  const popupState = usePopupState({ variant: 'popover', popupId: 'demoMenu' });
  useEffect(() => {
    if (popupState.isOpen) {
      track('User Action', {
        event_name: 'menu_drop_down',
        event_type: 'screen interaction',
        event_category: 'menu',
        event_location: window.location.pathname,
      });
    }
  }, [popupState.isOpen, track]);

  return (
    <AppHeader position="static" id="authenticatedHeader">
      <Toolbar>
        <LogoContainer>
          <Link to="/" style={{ lineHeight: 0 }} onClick={startViewTransition}>
            <Logo
              src={exosLogo}
              srcSet={`${exosLogo} 1x,${exosLogo2x} 2x`}
              alt="Exos"
            />
          </Link>
        </LogoContainer>
        <Button
          aria-label="menu"
          aria-controls="simple-menu"
          endIcon={
            <FontAwesomeIcon
              icon={faChevronDown}
              size="lg"
              color={theme.colors.gray[900]}
              style={{ marginBottom: 3, marginLeft: theme.baseUnit / 2 }}
            />
          }
          disableRipple
          style={{
            overflowWrap: 'anywhere',
          }}
          {...bindTrigger(popupState)}
        >
          {userData?.self?.email}
        </Button>
        <Menu
          keepMounted
          {...bindMenu(popupState)}
          classes={{ paper: 'top-0', list: 'no-pad' }}
        >
          <ListItem
            onClick={() => {
              popupState.close();
            }}
          >
            <ListItemIcon>
              <Avatar
                style={{ marginRight: theme.baseUnit }}
                alt={`${userData?.self?.firstName} ${userData?.self?.lastName}`}
                src={avatar}
              />
            </ListItemIcon>
            <ListItemText primary={userData?.self?.email} />
            <FontAwesomeIcon
              icon={faChevronUp}
              size="lg"
              color={theme.colors.gray[900]}
              style={{ marginLeft: theme.baseUnit }}
            />
          </ListItem>
          {showDeveloperMenu && (
            <ListItem onClick={() => navigate('/developer')}>
              <ListItemIcon>
                <FontAwesomeIcon
                  icon={faBug}
                  size="lg"
                  color={theme.colors.gray[900]}
                />
              </ListItemIcon>
              <ListItemText primary="Developer Menu" />
            </ListItem>
          )}
          <ListItem onClick={handleLogout}>
            <ListItemIcon>
              <FontAwesomeIcon
                icon={faArrowRightFromBracket}
                size="lg"
                color={theme.colors.gray[900]}
              />
            </ListItemIcon>
            <ListItemText primary="Sign out" />
          </ListItem>
        </Menu>
      </Toolbar>
      {showBanner && (
        <Banner
          text="Looking for Practices and Coaching Sessions? View your full Gameplan on the Exos app."
          id="practicesInfo"
        />
      )}
    </AppHeader>
  );
};

export default Header;

const AppHeader = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.colors.gray['1'],
  boxShadow: '0px 0px 12px 0px rgba(0,0,0,0.07)',
}));

const LogoContainer = styled(Box)({
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
});

const Logo = styled('img')({ width: '100px', height: 'auto' });

const ListItem = styled(MenuItem)(({ theme }) => ({
  height: theme.baseUnit * 8,
}));
